import { DIRECTIONS, COLORS } from './constants.js';

export class Snake {
    constructor() {
        this.reset();
    }
    
    reset() {
        // Start snake in center of grid
        const startX = Math.floor(20 / 2); // Assuming 20x20 grid
        const startY = Math.floor(15 / 2); // Assuming 15x15 grid
        
        this.segments = [
            { x: startX, y: startY },
            { x: startX - 1, y: startY },
            { x: startX - 2, y: startY }
        ];
        
        this.direction = DIRECTIONS.RIGHT;
        this.nextDirection = DIRECTIONS.RIGHT;
        this.color = COLORS.PAINT_WHITE;
        this.growPending = 0;
    }
    
    update() {
        // Update direction (prevent 180-degree turns)
        if (this.canChangeDirection(this.nextDirection)) {
            this.direction = this.nextDirection;
        }
        
        // Move snake
        const head = { ...this.segments[0] };
        head.x += this.direction.x;
        head.y += this.direction.y;
        
        this.segments.unshift(head);
        
        // Remove tail unless growing
        if (this.growPending > 0) {
            this.growPending--;
        } else {
            this.segments.pop();
        }
    }
    
    canChangeDirection(newDirection) {
        // Prevent 180-degree turns
        return !(this.direction.x === -newDirection.x && this.direction.y === -newDirection.y);
    }
    
    changeDirection(newDirection) {
        this.nextDirection = newDirection;
    }
    
    grow() {
        this.growPending++;
    }
    
    checkSelfCollision() {
        const head = this.segments[0];
        
        // Check if head collides with any body segment
        for (let i = 1; i < this.segments.length; i++) {
            if (head.x === this.segments[i].x && head.y === this.segments[i].y) {
                return true;
            }
        }
        
        return false;
    }
    
    getHead() {
        return this.segments[0];
    }
    
    getSegments() {
        return [...this.segments];
    }
    
    getLength() {
        return this.segments.length;
    }
    
    setColor(color) {
        this.color = color;
    }
    
    render(ctx, gridSize, color = this.color) {
        // Render snake segments with paint trail effect
        this.segments.forEach((segment, index) => {
            const x = segment.x * gridSize;
            const y = segment.y * gridSize;
            
            // Calculate alpha for trail effect (head is fully opaque, tail fades)
            const alpha = Math.max(0.3, 1 - (index * 0.1));
            
            // Draw segment with paint-like effect
            this.drawPaintSegment(ctx, x, y, gridSize, color, alpha, index === 0);
        });
    }
    
    drawPaintSegment(ctx, x, y, size, color, alpha, isHead) {
        ctx.save();
        
        // Set up paint effect
        ctx.globalAlpha = alpha;
        
        if (isHead) {
            // Draw snake head with paint marker shape
            this.drawPaintMarkerHead(ctx, x, y, size, color);
        } else {
            // Draw paint trail segment
            this.drawPaintTrail(ctx, x, y, size, color);
        }
        
        ctx.restore();
    }
    
    drawPaintMarkerHead(ctx, x, y, size, color) {
        // Paint marker head (wider at front)
        const centerX = x + size / 2;
        const centerY = y + size / 2;
        
        // Main body of marker
        ctx.fillStyle = color;
        ctx.fillRect(x + 2, y + 2, size - 4, size - 4);
        
        // Paint tip (slightly darker)
        ctx.fillStyle = this.darkenColor(color, 0.2);
        ctx.fillRect(x + 4, y + 4, size - 8, size - 8);
        
        // Highlight
        ctx.fillStyle = this.lightenColor(color, 0.3);
        ctx.fillRect(x + 2, y + 2, size - 4, 2);
        
        // Add paint drop effect
        ctx.beginPath();
        ctx.arc(centerX, centerY + size / 3, size / 8, 0, Math.PI * 2);
        ctx.fillStyle = this.darkenColor(color, 0.1);
        ctx.fill();
    }
    
    drawPaintTrail(ctx, x, y, size, color) {
        // Paint trail with slight texture
        const centerX = x + size / 2;
        const centerY = y + size / 2;
        
        // Main trail
        ctx.fillStyle = color;
        ctx.fillRect(x + 3, y + 3, size - 6, size - 6);
        
        // Add some paint texture (small random dots)
        ctx.fillStyle = this.darkenColor(color, 0.1);
        for (let i = 0; i < 3; i++) {
            const dotX = x + 4 + Math.random() * (size - 8);
            const dotY = y + 4 + Math.random() * (size - 8);
            ctx.beginPath();
            ctx.arc(dotX, dotY, 1, 0, Math.PI * 2);
            ctx.fill();
        }
        
        // Highlight
        ctx.fillStyle = this.lightenColor(color, 0.2);
        ctx.fillRect(x + 3, y + 3, size - 6, 1);
    }
    
    darkenColor(color, amount) {
        // Convert hex to RGB
        const hex = color.replace('#', '');
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);
        
        // Darken
        const newR = Math.floor(r * (1 - amount));
        const newG = Math.floor(g * (1 - amount));
        const newB = Math.floor(b * (1 - amount));
        
        // Convert back to hex
        return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
    }
    
    lightenColor(color, amount) {
        // Convert hex to RGB
        const hex = color.replace('#', '');
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);
        
        // Lighten
        const newR = Math.floor(r + (255 - r) * amount);
        const newG = Math.floor(g + (255 - g) * amount);
        const newB = Math.floor(b + (255 - b) * amount);
        
        // Convert back to hex
        return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
    }
}
